import { motion } from "framer-motion";

export const WhyChooseSection = () => {
  return (
    <section className="relative isolate flex w-full flex-col items-start overflow-hidden bg-[linear-gradient(180deg,rgba(238,240,241,0.3),#fff)] py-16 text-left font-sans text-[12px] text-[#0a3a4d] md:py-24">
      {/* Soft teal wave background */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(29,175,146,0.12)_0%,transparent_70%)] opacity-60" />

      <div className="relative mx-auto px-6 lg:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-xl space-y-8"
          >
            {/* Small Badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-npsText/20 bg-npsText/10 px-4 py-2 font-sans text-npsText backdrop-blur-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#66A0CD"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-award h-4 w-4"
              >
                <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526" />
                <circle cx="12" cy="8" r="6" />
              </svg>
              <span className="text-sm font-medium text-npsText">About Us</span>
            </div>

            {/* Heading */}
            <h1 className="font-display text-3xl font-bold leading-tight text-black md:text-4xl lg:text-5xl">
              Why Choose <span className="text-npsText">FinEase?</span>
            </h1>

            {/* Paragraph */}
            <p className="space-y-4 font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
              FinEase simplifies wealth creation with a multi-asset investment
              platform offering opportunities in{" "}
              <strong className="font-sans text-black">
                Equity, Mutual Fund, Bond, IPO, FD, Digital Gold, NPS, and
                Unlisted Shares.
              </strong>
            </p>

            <p className="space-y-4 font-sans text-base leading-relaxed text-muted-foreground md:text-lg">
              We simplify investing by providing accessible, transparent
              analytics and financial information, empowering you to confidently
              navigate your financial journey.
            </p>

            {/* Check List */}
            <div className="grid grid-cols-1 gap-4 text-muted_foreground sm:grid-cols-2">
              {[
                "SEBI & RBI compliant platform",
                "Bank-grade security encryption",
                "Instant KYC verification",
                "Dedicated relationship manager",
              ].map(text => (
                <div key={text} className="flex items-start gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-circle-check h-5 w-5 text-green-600"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  <span className="font-sans text-sm font-medium text-muted_foreground">
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/* Stats Grid */}
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                {
                  value: "50K+",
                  label: "Happy Investors",
                  icon: "/assets/Users.svg",
                  iconBg: "bg-blue-50 text-blue-600",
                },
                {
                  value: "₹100Cr+",
                  label: "Assets Managed",
                  icon: "/assets/saild.svg",
                  iconBg: "bg-green-50 text-green-600",
                },
                {
                  value: "4.8",
                  label: "User Rating",
                  icon: "/assets/Star.svg",
                  iconBg: "bg-yellow-50 text-yellow-600",
                },
                {
                  value: "24/7",
                  label: "Support",
                  icon: "/assets/Clock.svg",
                  iconBg: "bg-red-50 text-red-600",
                },
              ].map(item => (
                <div
                  key={item.value}
                  className="group h-[153.8px] w-full cursor-pointer rounded-2xl border border-[rgba(229,232,235,0.5)] bg-white text-center font-sans text-[#0a3a4d] transition-all duration-300 ease-out hover:-translate-y-2 hover:border-npsText/40 hover:shadow-xl"
                >
                  {/* Icon Container */}
                  <div
                    className={`mx-auto mb-4 mt-4 flex h-10 w-10 items-center justify-center rounded-xl shadow-sm transition-transform duration-300 group-hover:scale-110 ${item.iconBg} `}
                  >
                    <img src={item.icon} alt={item.value} className="h-5 w-5" />
                  </div>

                  {/* Value */}
                  <p className="mb-1 text-[24px] font-semibold text-foreground">
                    {item.value}
                  </p>

                  {/* Label */}
                  <p className="text-sm text-muted-foreground">{item.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT ILLUSTRATION */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative flex justify-center lg:justify-end"
          >
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full bg-primary/20 opacity-40 blur-3xl" />

            {/* Illustration */}
            <motion.img
              src="/assets/aboutFineaseIllustration.jpg"
              alt="Investment illustration"
              className="relative w-full max-w-sm drop-shadow-2xl md:max-w-md lg:max-w-lg"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Floating small card — IPO returns */}
            <div className="absolute -left-6 -top-6 hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-xl lg:block">
              <p className="text-xs font-medium text-gray-500">IPO Returns</p>
              <p className="text-lg font-bold text-green-600">+45.2%</p>
            </div>

            {/* Floating card — FD interest */}
            <div className="absolute bottom-8 right-2 hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-xl lg:block">
              <p className="text-xs font-medium text-gray-500">FD Interest</p>
              <p className="text-lg font-bold text-green-600">9.10% p.a.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
